import type { User } from '@shared/schemas';
import type { User as SupabaseAuthUser } from '@supabase/supabase-js';
import { coerceRole } from '@shared/access/roles';
import { supabase } from '../supabaseClient';

/**
 * Current-user endpoint — replaces GET /api/auth/me (slot SM.3).
 *
 * The shared `User` shape is assembled from two Supabase sources:
 *   · auth.users        — id, email, created_at, display name (user_metadata)
 *   · public.profiles   — locale, timezone
 * Display name is canonical in user_metadata (set at signup / edited in the
 * SM.3b profile screen); profiles holds only the cron-queryable fields.
 */

const FALLBACK_LOCALE = 'ru' as const;
const FALLBACK_TZ = 'Europe/Moscow';
const FALLBACK_NAME = 'Studyer';

type ProfileRow = { locale?: unknown; timezone?: unknown; role?: unknown } | null;

/** Assemble the shared `User` from a Supabase auth user + its profiles row. */
export async function assembleUser(authUser: SupabaseAuthUser): Promise<User> {
  const { data: profile } = await supabase
    .from('profiles')
    .select('locale, timezone, role')
    .eq('id', authUser.id)
    .maybeSingle();

  return buildUser(authUser, profile);
}

/**
 * User assembled WITHOUT the profiles enrichment fetch (profile = null → all
 * fallbacks). Use when that second network call must NOT be able to fail the
 * caller — e.g. right after a completed `updateUser` password change, where a
 * profile-fetch blip must not report the saved password as "Load failed"
 * (prod 13 AUG). role/locale refill on the next hydration.
 */
export function minimalUser(authUser: SupabaseAuthUser): User {
  return buildUser(authUser, null);
}

/** Build the shared `User` from an auth user + an optional profiles row. */
function buildUser(authUser: SupabaseAuthUser, profile: ProfileRow): User {
  const meta = (authUser.user_metadata ?? {}) as Record<string, unknown>;
  const metaName =
    (typeof meta.display_name === 'string' && meta.display_name) ||
    (typeof meta.full_name === 'string' && meta.full_name) ||
    (typeof meta.name === 'string' && meta.name) ||
    '';
  // `split('@')[0]` is `string | undefined` only under noUncheckedIndexedAccess
  // (a non-empty string always yields index 0) — `?? FALLBACK_NAME` keeps the
  // type honest so `displayName` resolves to a guaranteed string.
  const fallbackName = authUser.email
    ? authUser.email.split('@')[0] ?? FALLBACK_NAME
    : FALLBACK_NAME;
  const locale = profile?.locale;
  // Access tier · centralized coercion (known key → itself · legacy 'full' → 'pro' ·
  // else 'trial' least-privilege). See shared/access/roles.ts.
  const role = coerceRole(profile?.role);

  return {
    id: authUser.id,
    email: authUser.email ?? undefined,
    displayName: metaName || fallbackName,
    locale: locale === 'ru' || locale === 'tr' || locale === 'en' ? locale : FALLBACK_LOCALE,
    timezone: typeof profile?.timezone === 'string' ? profile.timezone : FALLBACK_TZ,
    createdAt: authUser.created_at,
    // Access tier from profiles.role · absent/unknown ⇒ 'trial' (least privilege).
    role,
  };
}

/** Current authenticated user, or null when there is no active session. */
export async function fetchCurrentUser(): Promise<User | null> {
  const { data, error } = await supabase.auth.getUser();
  if (error || !data.user) return null;
  return assembleUser(data.user);
}

export const meEndpoints = {
  /** Current authenticated user · throws when no session (parity with /me 401). */
  async get(): Promise<User> {
    const user = await fetchCurrentUser();
    if (!user) throw new Error('Сессия не найдена · войди заново');
    return user;
  },
};
